import { useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { useMemo } from "react";
import contextConfig from "../config/CopilotContextConfig.json";

// Final Page Context formed for the given page and tab
export interface PageContext {
  pageName: string;
  tabName: string;
  urlFilters: Record<string, any>;
  endpointData: EndpointData[];
}

export interface EndpointData {
  fullEndpoint: string; // Format: "endpointName/dataPath"
  dataExtractor: DataExtractor;
  data: string;
}

// Configuration for data extraction from APIs RTK query cache
interface DataExtractor {
  endpoint: string;
  label: string;
  dataPath: string;
  keys: string[];
  maxRecords: number;
  orderBy: string;
  description: string;
  kustoObjects: string[];
}

// Configuration for sections within a tab
interface SectionConfig {
  displayName: string;
  description: string;
  apiSources: Record<string, DataExtractor[]>;
}

// Configuration for tabs within a page
interface TabConfig {
  displayName: string;
  description: string;
  apiSources: Record<string, DataExtractor[]>;
  sections: Record<string, SectionConfig>;
}

// Configuration for each page
interface PageConfig {
  displayName: string;
  description: string;
  apiSources: Record<string, DataExtractor[]>;
  tabs: Record<string, TabConfig>;
}

/**
 * Custom hook that extracts current page context from URL and RTK Query cache
 * This hook reads existing Redux state without requiring any modifications to other components
 */
export const useCurrentPageContext = (): PageContext => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get all API states from Redux store - using exact reducerPath names from slices
  const common = useAppSelector(state => (state as any).common);
  const home = useAppSelector(state => (state as any).home);
  const aiHub = useAppSelector(state => (state as any).aiHub);
  const explorerCustomers = useAppSelector(state => (state as any).explorerCustomers);
  const explorePartners = useAppSelector(state => (state as any).explorePartners);
  const exploreProducts = useAppSelector(state => (state as any).exploreProducts);
  const exploreReports = useAppSelector(state => (state as any).exploreReports);
  const exploreFeatureRequests = useAppSelector(state => (state as any).exploreFeatureRequests);
  const baselineSecurityHealth = useAppSelector(state => (state as any).baselineSecurityHealth);

  const context = useMemo(() => {
    const apiStates: Record<string, any> = {
      common,
      home,
      aiHub,
      explorerCustomers,
      explorePartners,
      exploreProducts,
      exploreReports,
      exploreFeatureRequests,
      baselineSecurityHealth,
    };

    const metadata: PageContext = {
      pageName: "Home", // Default value
      tabName: "",
      urlFilters: {},
      endpointData: []
    };

    // Determine current page from URL
    const path = location.pathname;
    if (path.includes("/customer")) metadata.pageName = "Customer";
    else if(path.includes("/customersearch")) metadata.pageName = "CustomerSearch";
    else if (path.includes("/products")) metadata.pageName = "Products";
    else if (path.includes("/productview")) metadata.pageName = "ProductView";
    else if (path.includes("/aihub")) metadata.pageName = "AIHub";
    else if (path.includes("/reportcatalog")) metadata.pageName = "ReportCatalog";
    else if (path.includes("/reportview")) metadata.pageName = "ReportView";
    else if (path.includes("/partnersearch")) metadata.pageName = "PartnerSearch";
    else if (path.includes("/partners")) metadata.pageName = "Partners";
    else if (path.includes("/featurerequestsearch")) metadata.pageName = "FeatureRequestSearch";
    else if (path.includes("/featureflow")) metadata.pageName = "FeatureFlow";
    else if (path.includes("/productscorecard")) metadata.pageName = "ProductScoreCard";

    // Unique Ids from URL
    const tenantId = searchParams.get("id");
    const productid = searchParams.get("productid");
    const reportid = searchParams.get("reportid");
    const partnerOneId = searchParams.get("partnerOneId");
    const mpnId = searchParams.get("mpnId");

    if (tenantId) metadata.urlFilters.tenantId = tenantId;
    if (productid) metadata.urlFilters.productid = productid;
    if (reportid) metadata.urlFilters.reportid = reportid;
    if (partnerOneId) metadata.urlFilters.partnerOneId = partnerOneId;
    if (mpnId) metadata.urlFilters.mpnId = mpnId;

    // Get tab and section from search params
    const tabName = searchParams.get("tab") || "";
    const sectionName = searchParams.get("section") || "";

    // Get hierarchical configuration by merging page -> tab -> section
    const pageConfig = (contextConfig as Record<string, PageConfig>)[metadata.pageName];

    // Merge configs from page -> tab -> section hierarchy
    const mergeConfigs = (...configs: Array<{ displayName?: string; description?: string; apiSources?: Record<string, DataExtractor[]> }>): { displayName: string; description: string; apiSources: Record<string, DataExtractor[]> } => {
      const merged = { displayName: "", description: "", apiSources: {} as Record<string, DataExtractor[]> };
      const apiSourceDescriptions: string[] = [];

      configs.forEach(config => {
        if (config.displayName) merged.displayName = config.displayName;

        if (config.apiSources) {
          // Merge apiSources by combining arrays for the same API key
          Object.entries(config.apiSources).forEach(([apiKey, extractors]) => {
            if (merged.apiSources[apiKey]) {
              merged.apiSources[apiKey] = [...merged.apiSources[apiKey], ...extractors];
            } else {
              merged.apiSources[apiKey] = [...extractors];
            }

            // Collect descriptions from API sources
            extractors.forEach((extractor) => {
              if (extractor.description && extractor.description.trim()) {
                const cleanDescription = extractor.description.trim();
                if (!apiSourceDescriptions.includes(cleanDescription)) {
                  apiSourceDescriptions.push(cleanDescription);
                }
              }
            });
          });
        }
      });

      // Combine all API source descriptions as numbered bulleted list
      if (apiSourceDescriptions.length > 0) {
        merged.description = apiSourceDescriptions.map((desc, index) => {
          // Remove existing numbering pattern (e.g., "1. ", "2. ", etc.) from the beginning
          const cleanDesc = desc.replace(/^\d+\.\s*/, "");
          return `${index + 1}. ${cleanDesc}`;
        }).join("\n");
      } else {
        merged.description = "";
      }

      return merged;
    };

    let currentConfig = { displayName: "", description: "", apiSources: {} as Record<string, DataExtractor[]> };

    if (pageConfig) {
      // Start with page-level config
      currentConfig = mergeConfigs(pageConfig);

      // Merge tab-level config if exists
      if (tabName && pageConfig.tabs?.[tabName]) {
        currentConfig = mergeConfigs(currentConfig, pageConfig.tabs[tabName]);

        // Merge section-level config if exists
        if (sectionName && pageConfig.tabs[tabName].sections?.[sectionName]) {
          currentConfig = mergeConfigs(currentConfig, pageConfig.tabs[tabName].sections[sectionName]);
        }
      }
    }
    // Fallback to default if no config found
    if (!currentConfig.displayName) {
      const defaultConfig = (contextConfig as any)["default"];
      if (defaultConfig) currentConfig = mergeConfigs(defaultConfig);
    }

    metadata.tabName = currentConfig.displayName || sectionName || tabName;

    // Extract and filter data based on merged configuration
    const extractedData: EndpointData[] = [];

    if (currentConfig.apiSources && Object.keys(currentConfig.apiSources).length > 0) {
      // Iterate over each API source
      Object.entries(currentConfig.apiSources).forEach(([apiName, extractors]) => {
        const apiState = apiStates[apiName];  // Derive the relevant API state from Redux store

        if (!apiState?.queries && !apiState?.mutations) return;  // No data for this API in the store

        // Iterate over each data extractor (endpoint) for this API source
        extractors.forEach((extractor) => {
          // Navigate the entire data path
          const endpointParts = extractor.endpoint.split(".");
          const pathParts = extractor.dataPath.split(".");
          let current: any = apiState;

          // First part should be 'queries' or 'mutations', then find matching endpoint
          if (endpointParts.length > 0 && (endpointParts[0] === "queries" || endpointParts[0] === "mutations")) { // Either for GET or POST queries
            const sourceType = endpointParts[0];
            if (!apiState[sourceType]) return;

            // Find the most recent entry by endpoint (based on fulfilledTimeStamp)
            const matchingEntries = Object.entries(apiState[sourceType]).filter(([key]) =>
              key.startsWith(endpointParts[1])
            );

            if (matchingEntries.length === 0) return;

            // Sort by fulfilledTimeStamp (descending) to get the most recent entry
            const [, query] = matchingEntries
              .sort(([, queryA], [, queryB]) => {
                const timestampA = (queryA as any)?.fulfilledTimeStamp || 0;
                const timestampB = (queryB as any)?.fulfilledTimeStamp || 0;
                return timestampB - timestampA; // Descending order (newest first)
              })[0];
            current = (query as any);
            // Navigate the datapath further
            for (let i = 0; i < pathParts.length; i++) {
              current = current?.[pathParts[i]];
              if (current === undefined) return;
            }
          } else {
            // Fallback: navigate entire path as-is
            for (const part of pathParts) {
              current = current?.[part];
              if (current === undefined) return;
            }
          }

          // Filter keys and limit records (empty keys array = return all keys)
          const filterData = (data: any): any => {
            let result = data;

            if (Array.isArray(result)) {
              // Sort by orderBy column if specified (descending order)
              if (extractor.orderBy && extractor.orderBy.trim() !== "") {
                result = [...result].sort((a, b) => {
                  const aVal = a[extractor.orderBy];
                  const bVal = b[extractor.orderBy];

                  // Handle null/undefined values
                  if (aVal == null && bVal == null) return 0;
                  if (aVal == null) return 1;
                  if (bVal == null) return -1;

                  // Compare values in descending order (works for strings, numbers, dates)
                  if (aVal < bVal) return 1;
                  if (aVal > bVal) return -1;
                  return 0;
                });
              }

              // Filter to only specified keys (if keys specified)
              if (extractor.keys?.length > 0) {
                result = result.map((item: any) => {
                  if (typeof item === "object" && item !== null) {
                    const filtered: any = {};
                    extractor.keys.forEach(key => { if (key in item) filtered[key] = item[key]; });
                    return filtered;
                  }
                  return item;
                });
              }
              // Limit number of records
              if (extractor.maxRecords) result = result.slice(0, extractor.maxRecords);
            } else if (typeof result === "object" && result !== null && extractor.keys?.length > 0) {
              // Filter to only specified keys in object
              const filtered: any = {};
              extractor.keys.forEach(key => { if (key in result) filtered[key] = result[key]; });
              result = filtered;
            }

            return result;
          };

          extractedData.push({
            fullEndpoint: `${extractor.endpoint}/${extractor.dataPath}`,
            dataExtractor: extractor,
            data: JSON.stringify(filterData(current), null, 2)
          });
        });
      });
    }

    metadata.endpointData = extractedData;

    return metadata;
  }, [location, searchParams, common, home, aiHub, explorerCustomers, explorePartners, exploreProducts, exploreReports, exploreFeatureRequests, baselineSecurityHealth]);

  return context;
};
